// Putting an attention card away for the rest of this sign-in.
//
// THE CARDS STAY IN THE DOCUMENT. `hidden` is the browser's own hide, the
// same choice InfoTip makes, so a check that counts refusals still finds
// every sentence and a screen reader is not left with an alert that has
// been dismissed in one tree and not the other.
//
// THE SCOPE IS THE SIGN-IN. App wraps the signed-in tree with the current
// `sessionId`. Surfaces read that rather than reaching for the store, so a
// check that keeps the session in memory still dismisses against the id
// the session actually has.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ModeIcon } from "@nova/desk-ui/parts";
import { readDismissed, writeDismissed } from "../session/dismissed.js";
import { browserSessionStore } from "../session/store.js";

type DismissedApi = {
  readonly has: (id: string) => boolean;
  readonly dismiss: (id: string) => void;
};

const DismissedContext = createContext<DismissedApi | null>(null);

export function DismissedScope({
  sessionId,
  children,
}: {
  sessionId: string;
  children: ReactNode;
}): ReactNode {
  const store = useMemo(() => browserSessionStore(), []);
  const [held, setHeld] = useState(() => readDismissed(sessionId, store));

  useEffect(() => {
    setHeld(readDismissed(sessionId, store));
  }, [sessionId, store]);

  const dismiss = useCallback((id: string) => {
    writeDismissed(sessionId, id, store);
    setHeld((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, [sessionId, store]);

  const value = useMemo<DismissedApi>(
    () => ({
      has: (id) => held.has(id),
      dismiss,
    }),
    [held, dismiss],
  );

  return <DismissedContext.Provider value={value}>{children}</DismissedContext.Provider>;
}

export function useDismissed(): DismissedApi {
  const ctx = useContext(DismissedContext);
  if (ctx === null) {
    return { has: () => false, dismiss: () => {} };
  }
  return ctx;
}

/** The X in the top-right of an attention card. */
export function AttClose({ id }: { id: string }): ReactNode {
  const { dismiss } = useDismissed();
  return (
    <button type="button" className="att-x" aria-label="Close this" onClick={() => dismiss(id)}>
      <ModeIcon kind="close" />
    </button>
  );
}
